import { Icon } from "@material-ui/core";
import React, { Component, Fragment } from "react";
import styled from "styled-components";
import _ from "lodash";

import { LibraryView } from "common/constants";
import { ActionPermissionsObject, PermissionActionType, WorkspacePlanTier, WorkspaceResourceType } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { OnMountedReporter } from "js/core/utilities/useMountedAt";
import { SideBarItem } from "js/editor/PresentationLibrary/PresentationLibrarySideBar";
import PresentationLibraryViewSelect from "js/editor/PresentationLibrary/PresentationLibraryViewSelect";
import UpgradeIcon from "js/Components/UpgradeIcon";
import UpgradeSection from "js/Components/UpgradeSection";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { FlexSpacer, Gap10 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Divider, UIPaneSideBar, UIPaneWithSideBar } from "js/react/components/UiComponents";

import { disposeAllIndexes } from "js/react/views/AddSlide/DataServices";
import { t } from "js/i18n";
import { themeColors } from "js/react/sharedStyles";

import PlayerSettingsPane from "./panes/PlayerSettingsPane";
import PresentationTemplatesPane from "../SharedResources/PresentationTemplatesPane";
import TeamBrandingGuardrailsPane from "./panes/TeamBrandingGuardrailsPane";
import TeamFoldersPane from "./panes/TeamFoldersPane";
import TeamSharedThemesPane from "./panes/TeamSharedThemesPane";
import SharedSlidesPane, { ALL_SLIDES_RESOURCE_TYPES, SHARED_SLIDES_RESOURCE_TYPES, SLIDE_TEMPLATES_RESOURCE_TYPES } from "../SharedResources/SharedSlidesPane";
import SharedAssetsPane from "../SharedResources/SharedAssetsPane";

export const TeamResourceTypes = {
    PRESENTATION_TEMPLATES: "presentation-templates",
    TEAM_THEMES: "themes",
    TEAM_FOLDERS: "folders",
    TEAM_BRANDING_GUARDRAILS: "branding-guardrails",

    // Assets Resources
    TEAM_ASSETS: "assets",
    TEAM_ASSETS_IMAGES: "assets-images",
    SHARED_ASSETS_LOGOS: "shared-logos",

    PLAYER_SETTINGS: "player-settings",

    // slide Resources
    SHARED_SLIDES: "shared-slides",
    SLIDE_TEMPLATES: "slide-templates",
    SLIDES: "slides"
};

const Styler = styled.div`
    width: 100%;
    height: 100%;
    display: flex;

    .search-container {
        padding-top: 5px;
    }
`;

const SidebarUpgradeFooter = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const StyledIcon = styled(Icon)`
    &&& {
        font-size: 20px;
    }
`;

class TeamResources extends Component {
    state = {
        selectedPane: TeamResourceTypes.PRESENTATION_TEMPLATES,
    }

    constructor(props) {
        super(props);

        if (props.pane) {
            this.state.selectedPane = props.pane;
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.pane != prevProps.pane) {
            this.setState({ selectedPane: this.props.pane });
        }
    }

    componentWillUnmount() {
        disposeAllIndexes();
    }

    handleSelectResourceType = (type, folderId = null) => {
        if (Array.isArray(type)) {
            if (_.isEqual(type, SHARED_SLIDES_RESOURCE_TYPES)) {
                type = TeamResourceTypes.SHARED_SLIDES;
            } else if (_.isEqual(type, SLIDE_TEMPLATES_RESOURCE_TYPES)) {
                type = TeamResourceTypes.SLIDE_TEMPLATES;
            } else if (_.isEqual(type, ALL_SLIDES_RESOURCE_TYPES)) {
                type = TeamResourceTypes.SLIDES;
            }
        }

        AppController.showTeamResources({ pane: type, folderId });
    }

    handleSelectPresentationTemplate = (presentationId, isDoubleClick, selectedFolderId) => {
        if (!presentationId) {
            return;
        }

        const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
        if (isDoubleClick && canManageSharedResources) {
            AppController.showEditor({ presentationId, folderId: selectedFolderId });
        }
    }

    getWorkspaceResourceTypes = type => {
        if (type === TeamResourceTypes.SLIDE_TEMPLATES) {
            return [WorkspaceResourceType.SLIDE_TEMPLATE];
        }

        if (type === TeamResourceTypes.SHARED_SLIDES) {
            return [WorkspaceResourceType.SLIDE];
        }

        return [WorkspaceResourceType.SLIDE, WorkspaceResourceType.SLIDE_TEMPLATE];
    }

    // NOTE: We have to use `className="selected"` instead of `props.selected`
    //   because a bug with `styled and` the application of `.dark-mode`
    selectedClass(filter) {
        return filter ? "selected" : "";
    }

    render() {
        const { onReady = () => { } } = this.props;
        const { selectedPane } = this.state;
        const { folderId } = this.props;
        const { onSwitchType, sideBarWidth, onResizeSidebar } = this.props;

        const isBasic = WorkspaceController.plan.tier === WorkspacePlanTier.BASIC;

        const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
        const canUpgradeToManageSharedResources = isBasic || WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
        const canManagePlayerSettings = WorkspaceController.actionPermissions[ActionPermissionsObject.PLAYER_SETTINGS].manage;
        const canUpgradeToManagePlayerSettings = (canUpgradeToManageSharedResources && canManagePlayerSettings) || (canManageSharedResources && WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.PLAYER_SETTINGS, PermissionActionType.MANAGE));
        const canManageBrandGuardrails = WorkspaceController.actionPermissions[ActionPermissionsObject.BRAND_GUARDRAILS].manage;
        const canUpgradeToManageBrandGuardrails = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.BRAND_GUARDRAILS, PermissionActionType.MANAGE);

        return (
            <UIPaneWithSideBar>
                <UIPaneSideBar
                    style={{ width: sideBarWidth }}
                    onResize={onResizeSidebar}
                >
                    <PresentationLibraryViewSelect libraryView={LibraryView.TEAM_RESOURCES} onSwitchType={onSwitchType} />
                    {(canManageSharedResources || canUpgradeToManageSharedResources) && <>
                        <SideBarItem
                            className={this.selectedClass(selectedPane == TeamResourceTypes.PRESENTATION_TEMPLATES)}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.PRESENTATION_TEMPLATES)}
                            data-test-id="sidebar-nav-team-templates"
                        >
                            <StyledIcon className="material-symbols-outlined">video_library</StyledIcon>
                            {t("Team Templates")}
                        </SideBarItem>
                        <SideBarItem
                            className={this.selectedClass(
                                selectedPane === TeamResourceTypes.SLIDE_TEMPLATES ||
                                selectedPane === TeamResourceTypes.SHARED_SLIDES ||
                                selectedPane === TeamResourceTypes.SLIDES
                            )}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.SLIDES)}
                            data-test-id="sidebar-nav-team-slides"
                        >
                            <StyledIcon className="material-symbols-outlined">library_add</StyledIcon>
                            {t("Team Slides")}
                        </SideBarItem>
                        <Divider margin={10} />
                        <SideBarItem
                            className={this.selectedClass(selectedPane == TeamResourceTypes.TEAM_THEMES)}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.TEAM_THEMES)}
                            data-test-id="sidebar-nav-team-themes"
                        >
                            <StyledIcon>color_lens</StyledIcon>
                            {t("Team Themes")}
                        </SideBarItem>
                        <SideBarItem
                            className={this.selectedClass(
                                selectedPane === TeamResourceTypes.TEAM_ASSETS ||
                                selectedPane === TeamResourceTypes.TEAM_ASSETS_IMAGES ||
                                selectedPane === TeamResourceTypes.SHARED_ASSETS_LOGOS
                            )}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.TEAM_ASSETS)}
                            data-test-id="sidebar-nav-team-assets"
                        >
                            <StyledIcon className="material-symbols-outlined">photo_library</StyledIcon>
                            {t("Team Assets")}
                        </SideBarItem>
                    </>}
                    <Divider margin={10} />
                    {(canManagePlayerSettings || canUpgradeToManagePlayerSettings) &&
                        <SideBarItem
                            className={this.selectedClass(selectedPane == TeamResourceTypes.PLAYER_SETTINGS)}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.PLAYER_SETTINGS)}
                            data-test-id="sidebar-nav-player-settings"
                        >
                            <StyledIcon>play_circle_outline</StyledIcon>
                            {t("Player Settings")}
                        </SideBarItem>
                    }
                    {(canManageBrandGuardrails || canUpgradeToManageBrandGuardrails) &&
                        <SideBarItem
                            className={this.selectedClass(selectedPane == TeamResourceTypes.TEAM_BRANDING_GUARDRAILS)}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.TEAM_BRANDING_GUARDRAILS)}
                            data-test-id="sidebar-nav-brand-guardrails"
                        >
                            <StyledIcon>lock_outline</StyledIcon>
                            {t("Brand Guardrails")}
                        </SideBarItem>
                    }
                    {WorkspaceController.isOwner &&
                        <Fragment>
                            <SideBarItem
                                className={this.selectedClass(selectedPane == TeamResourceTypes.TEAM_FOLDERS)}
                                onClick={() => this.handleSelectResourceType(TeamResourceTypes.TEAM_FOLDERS)}
                                data-test-id="sidebar-nav-team-folders"
                            >
                                <StyledIcon className="material-symbols-outlined">folder_shared</StyledIcon>
                                {t("Team Folders")}
                            </SideBarItem>
                        </Fragment>
                    }
                    {canUpgradeToManageSharedResources &&
                    <>
                        <FlexSpacer/>
                        <SidebarUpgradeFooter>
                            <Divider margin={0} color={themeColors.lightGray} />
                            <UpgradeSection
                                title={t("Unlock Shared Team Resources")}
                                body={t("Upgrade to a Team plan to share templates, themes, and assets—boosting productivity and brand consistency.")}
                                buttonText={t("Upgrade")}
                                buttonIcon={<UpgradeIcon />}
                                buttonOnClick={() => ShowDialog(ChangePlanDialog,
                                    {
                                        featuredPlan: WorkspacePlanTier.TEAM,
                                        analytics: { cta: "shared_team_resources" }
                                    })}
                            />
                        </SidebarUpgradeFooter>
                    </>}
                </UIPaneSideBar>

                <FlexBox fill vertical top>
                    <Styler>
                        <OnMountedReporter onMounted={onReady} />
                        {selectedPane === TeamResourceTypes.PRESENTATION_TEMPLATES &&
                            <PresentationTemplatesPane
                                editable={true}
                                onReady={onReady}
                                onSelect={this.handleSelectPresentationTemplate}
                                folderId={folderId}
                                onSelectFolder={this.handleSelectResourceType}
                            />
                        }
                        {(selectedPane === TeamResourceTypes.SLIDE_TEMPLATES ||
                        selectedPane === TeamResourceTypes.SHARED_SLIDES ||
                        selectedPane === TeamResourceTypes.SLIDES) &&
                            <SharedSlidesPane
                                isManageLibrary
                                resourceTypes={this.getWorkspaceResourceTypes(selectedPane)}
                                folderId={folderId}
                                onSelectFolder={this.handleSelectResourceType}
                            />
                        }
                        {selectedPane === TeamResourceTypes.TEAM_THEMES &&
                            <TeamSharedThemesPane />
                        }
                        {selectedPane === TeamResourceTypes.TEAM_FOLDERS &&
                            <TeamFoldersPane />
                        }
                        {selectedPane === TeamResourceTypes.TEAM_BRANDING_GUARDRAILS &&
                            <TeamBrandingGuardrailsPane />
                        }
                        {(selectedPane === TeamResourceTypes.TEAM_ASSETS ||
                        selectedPane === TeamResourceTypes.TEAM_ASSETS_IMAGES ||
                        selectedPane === TeamResourceTypes.SHARED_ASSETS_LOGOS) &&
                            <SharedAssetsPane
                                resourceType={selectedPane}
                                editable
                                folderId={folderId}
                                onSelectFolder={this.handleSelectResourceType}
                            />
                        }
                        {selectedPane === TeamResourceTypes.PLAYER_SETTINGS &&
                            <PlayerSettingsPane />
                        }
                    </Styler>
                </FlexBox>
            </UIPaneWithSideBar>
        );
    }
}

export default TeamResources;
